<script>
import Content from '@/services/Content'
import validationMessages from '@/components/validations'
import NotificationAudienceTargeting from '@/components/notification/NotificationAudienceTargeting'
import { required, requiredIf } from "vuelidate/lib/validators";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Uppy from '@uppy/core';
import AwsS3Multipart from '@uppy/aws-s3-multipart';
import {
    createUppyS3MultipartHandlers,
    UPLOAD_CHUNK_SIZE,
    uploadFileWithUppy,
} from '@/utils/uppyS3Multipart';

export default {
    components: {
        validationMessages,
        flatPickr,
        vueDropzone: vue2Dropzone,
        NotificationAudienceTargeting,
    },
    props: {
        // Allows the same modal to be reused for shorts. Defaults keep the videos page unchanged.
        modalId: { type: String, default: 'upload_video' },
        headerTitle: { type: String, default: 'Upload Video' },
        headerSubtitle: { type: String, default: 'Upload a new video to the feed' },
        // 'video' (default) posts to /content/videos, 'short' posts to /content/shorts.
        uploadMode: { type: String, default: 'video' },
    },
    data() {
        return {
            csrf_token: localStorage.getItem('csrf_token'),
            showModal: false,
            tryingToSubmit: false,
            submitted: false,
            title: "",
            description:"",
            videoFormat: 'mp4',
            m3u8Url: '',
            video:null,
            thumbnail:null,
            verticalImage:null,
            verticalImagePreviewUrl: null,
            verticalImageDeleted: false,
            tags:[],
            publish: null,
            date: '',
            time: '',
            isPremium: false,

            acceptedFileTypeErrorMessage: '',
            acceptedFileTypeError: false,
            acceptedFileSizeErrorMessage: '',
            acceptedFileSizeError: false,
            tagInput: '',
            videoPreviewUrl: null,
            thumbnailPreviewUrl: null,
            videoDeleted: false,
            thumbnailDeleted: false,
            accepted_file_size: 4096,
            isDragOver: false,
            uppy: null,
            videoUploadProgress: 0,
            // false | 'uploading' | 'optimizing' | 'saving'
            savePhase: false,
            dateconfig: {
                enableTime: false,
                dateFormat: "Y-m-d",
                minDate: new Date(),
            },
            timeconfig: {
                enableTime: true,
                noCalendar: true,
                time_24hr: true,
                minuteIncrement: 1,
                dateFormat: "H:i",
                allowInput: true
            },
            dropzoneOptions_video: {
                url: '/',
                autoProcessQueue: false,
                addRemoveLinks: true,
                thumbnailWidth: 800,
                thumbnailHeight: null,
                thumbnailMethod: 'contain',
                maxFilesize: 5120,
                acceptedFiles: 'video/mp4',
                maxFiles: 1,
            },
            dropzoneOptions_thumbnail: {
                url: '/',
                autoProcessQueue: false,
                addRemoveLinks: true,
                thumbnailWidth: 800,
                thumbnailHeight: null,
                thumbnailMethod: 'contain',
                maxFilesize: 4,
                acceptedFiles: 'image/jpeg,image/png,image/bmp,image/webp',
                maxFiles: 1,
            },
            dropzoneOptions_vertical: {
                url: '/',
                autoProcessQueue: false,
                addRemoveLinks: true,
                thumbnailWidth: 800,
                thumbnailHeight: null,
                thumbnailMethod: 'contain',
                maxFilesize: 4,
                acceptedFiles: 'image/jpeg,image/png,image/bmp,image/webp',
                maxFiles: 1,
            },

        };
    },
    validations: {
        title: { required },
        video: {
            required: requiredIf(function () { return this.videoFormat === 'mp4'; }),
        },
        m3u8Url: {
            required: requiredIf(function () { return this.videoFormat === 'm3u8'; }),
            validM3u8Url(value) {
                if (!value) return true;
                return /^https?:\/\/[^\s]+\.m3u8(\?.*)?$/i.test(value);
            },
        },
        thumbnail: { required },
        description: {},
        tags: {},
    },

    computed: {
        uploadBusy() {
            return this.tryingToSubmit;
        },
        submitButtonLabel() {
            if (!this.tryingToSubmit) {
                return 'Submit';
            }
            if (this.savePhase === 'uploading' || (
                this.videoFormat === 'mp4'
                && this.videoUploadProgress > 0
                && this.videoUploadProgress < 100
            )) {
                return `Uploading ${Math.round(this.videoUploadProgress)}%`;
            }
            if (this.savePhase === 'optimizing') {
                return 'Optimizing video…';
            }
            return 'Saving…';
        },
    },

    mounted() {
        const uploadFrom = this.uploadMode === 'short' ? 'shorts' : 'videos';
        const multipartHandlers = createUppyS3MultipartHandlers(uploadFrom);
        this.uppy = new Uppy({
            restrictions: {
                maxNumberOfFiles: 1,
                allowedFileTypes: ['video/mp4', 'video/*'],
            },
        }).use(AwsS3Multipart, {
            ...multipartHandlers,
            getChunkSize: () => UPLOAD_CHUNK_SIZE,
        });
        this.uppy.on('progress', (progress) => {
            this.videoUploadProgress = progress;
        });
    },

    beforeDestroy() {
        if (this.uppy) {
            this.uppy.close();
        }
    },
    
    methods: {

        addTag() {
            const tag = this.tagInput.trim().replace(/,+$/, '');
            if (tag && !this.tags.includes(tag)) {
                this.tags.push(tag);
            }
            this.tagInput = '';
        },
        removeTag(index) {
            this.tags.splice(index, 1);
        },

        onVideoFormatChange() {
            if (this.videoFormat === 'mp4') {
                this.m3u8Url = '';
            } else {
                this.clearVideo();
            }
        },

        formatApiError(error) {
            if (!error) return '';
            if (Array.isArray(error)) {
                return error.join(' ');
            }
            if (typeof error === 'object') {
                return Object.values(error).flat().join(' ');
            }
            return String(error);
        },

        async uploadVideo() {
            if (this.videoFormat === 'mp4' && (this.acceptedFileTypeError || this.acceptedFileSizeError)) return;

            this.tryingToSubmit = true;
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.tryingToSubmit = false;
                return;
            }

            const audienceError = this.$refs.audienceTargeting.validate();
            if (audienceError) {
                this.failedmsg('Audience', audienceError);
                this.tryingToSubmit = false;
                return;
            }

            let s3Key = null;
            if (this.videoFormat === 'mp4') {
                this.videoUploadProgress = 0;
                this.savePhase = 'uploading';
                try {
                    s3Key = await uploadFileWithUppy(this.uppy, this.video);
                } catch (error) {
                    this.failedmsg(
                        'Upload failed',
                        error?.message || 'The video could not be uploaded to storage. Please try again.'
                    );
                    this.tryingToSubmit = false;
                    this.savePhase = false;
                    this.videoUploadProgress = 0;
                    return;
                }
                this.savePhase = 'optimizing';
            } else {
                this.savePhase = 'saving';
            }

            const formData = new FormData();
            formData.append("title", this.title);
            formData.append("description", this.description);
            formData.append("video_format", this.videoFormat);
            if (this.videoFormat === 'm3u8') {
                formData.append("video_url", this.m3u8Url);
            } else {
                formData.append("s3_key", s3Key);
            }
            formData.append("image_file", this.thumbnail);
            if (this.uploadMode === 'short' && this.verticalImage) {
                formData.append("vertical_image_file", this.verticalImage);
            }
            this.tags.forEach((tag) => formData.append("tags[]", tag));
            if (this.date) {
                formData.append("published", this.time ? `${this.date} ${this.time}` : this.date);
            }
            formData.append("premium", this.isPremium ? 1 : 0);
            formData.append("csrf_token", this.csrf_token);
            this.$refs.audienceTargeting.appendToFormData(formData);

            const isShort = this.uploadMode === 'short';
            const request = isShort ? Content.uploadShort(formData) : Content.uploadVideo(formData);

            request
                .then(() => {
                    this.successmsg(isShort ? "Short Uploaded!" : "Video Uploaded!");
                    this.showModal = false;
                    this.refreshData();
                })
                .catch((error) => {
                    const body = error.response?.data;
                    let errorMsg = this.formatApiError(body?.error);
                    if (!errorMsg && error.response?.status === 413) {
                        errorMsg = 'Upload too large for the server (post_max_size exceeded).';
                    }
                    if (!errorMsg && !error.response) {
                        errorMsg = 'Network error or upload blocked before reaching the API.';
                    }
                    if (!errorMsg) {
                        errorMsg = isShort ? 'Failed to upload short' : 'Failed to upload video';
                    }
                    this.failedmsg(
                        this.savePhase === 'optimizing' ? 'Optimization failed' : 'Upload failed',
                        errorMsg
                    );
                })
                .finally(() => {
                    this.tryingToSubmit = false;
                    this.savePhase = false;
                    this.videoUploadProgress = 0;
                });
        },
        closeModal() {
            if (this.uploadBusy) {
                return;
            }
            this.showModal = false;
        },
        openDatePicker() {
            this.$refs.datePicker.$el.click();
        },
        openTimePicker() {
            this.$refs.timePicker.$el.click();
        },
        updatePublished() {
            if (this.date && this.time) {
                this.publish = `${this.date} ${this.time}`;
            } else if (this.date) {
                this.publish = this.date;
            } else {
                this.publish = null;
            }
        },
   
   
       
        removeVideo() {
            this.file = null;
            if (this.video) {
                URL.revokeObjectURL(this.video);
            }
            this.video = "";
            if (this.$refs['file-input']) {
                this.$refs['file-input'].value = '';
            }
            this.video_removed = true;
        },

        onModalShown() {
            if (this.$refs.audienceTargeting) {
                this.$refs.audienceTargeting.onShown();
            }
        },
        resetProps() {
            if (this.$refs.audienceTargeting) {
                this.$refs.audienceTargeting.reset();
            }
            this.submitted = false;
            this.title = '';
            this.description = '';
            this.videoFormat = 'mp4';
            this.m3u8Url = '';
            this.tags = [];
            this.tagInput = '';
            this.video = null;
            this.thumbnail = null;
            this.verticalImage = null;
            if (this.videoPreviewUrl) URL.revokeObjectURL(this.videoPreviewUrl);
            if (this.verticalImagePreviewUrl) URL.revokeObjectURL(this.verticalImagePreviewUrl);
            this.videoPreviewUrl = null;
            this.thumbnailPreviewUrl = null;
            this.verticalImagePreviewUrl = null;
            this.videoDeleted = false;
            this.thumbnailDeleted = false;
            this.verticalImageDeleted = false;
            this.isDragOver = false;
            this.$nextTick(() => {
                if (this.$refs.myVueDropzone_video_url) this.$refs.myVueDropzone_video_url.removeAllFiles();
                if (this.$refs.myVueDropzone_thumbnail_url) this.$refs.myVueDropzone_thumbnail_url.removeAllFiles();
                if (this.$refs.myVueDropzone_vertical_url) this.$refs.myVueDropzone_vertical_url.removeAllFiles();
            });
            this.publish = null;
            this.date = '';
            this.time = '';
            this.isPremium = false;
            this.videoUploadProgress = 0;
            this.savePhase = false;
        },

        selectFileVideoUrl(files) {
            if (this.uploadBusy) return;
            if (files.length > 1) {
                this.failedmsg('You can only have one video');
                files.slice(1).forEach((f) => this.$refs.myVueDropzone_video_url.removeFile(f));
            }
            const videoFile = files[0] || null;
            this.video = videoFile;
            this.videoDeleted = false;
            if (videoFile) {
                if (this.videoPreviewUrl) URL.revokeObjectURL(this.videoPreviewUrl);
                this.videoPreviewUrl = URL.createObjectURL(videoFile);
                this.generateThumbnailFromVideo(videoFile);
            }
        },
        clearVideo() {
            if (this.videoPreviewUrl) URL.revokeObjectURL(this.videoPreviewUrl);
            this.videoPreviewUrl = null;
            this.video = null;
            this.videoDeleted = true;
            this.$refs.myVueDropzone_video_url.removeAllFiles();
        },
        selectFileThumbnailUrl(files) {
            if (this.uploadBusy) return;
            if (files.length > 1) {
                this.failedmsg('You can only have one thumbnail');
                files.slice(1).forEach((f) => this.$refs.myVueDropzone_thumbnail_url.removeFile(f));
            }
            const thumbnailFile = files[0] || null;
            if (!thumbnailFile) {
                this.thumbnail = null;
                return;
            }
            this.thumbnailDeleted = false;

            if (this.uploadMode === 'short') {
                this.normalizeImageToVertical(thumbnailFile)
                    .then((verticalFile) => {
                        this.thumbnail = verticalFile;
                        if (this.thumbnailPreviewUrl) URL.revokeObjectURL(this.thumbnailPreviewUrl);
                        this.thumbnailPreviewUrl = URL.createObjectURL(verticalFile);
                    })
                    .catch(() => {
                        this.thumbnail = thumbnailFile;
                        this.thumbnailPreviewUrl = URL.createObjectURL(thumbnailFile);
                    });
                return;
            }

            this.thumbnail = thumbnailFile;
            this.thumbnailPreviewUrl = URL.createObjectURL(thumbnailFile);
        },
        clearThumbnail() {
            if (this.thumbnailPreviewUrl) URL.revokeObjectURL(this.thumbnailPreviewUrl);
            this.thumbnailPreviewUrl = null;
            this.thumbnail = null;
            this.thumbnailDeleted = true;
            this.$refs.myVueDropzone_thumbnail_url.removeAllFiles();
        },
        selectFileVerticalUrl(files) {
            if (this.uploadBusy) return;
            if (files.length > 1) {
                this.failedmsg('You can only have one vertical image');
                files.slice(1).forEach((f) => this.$refs.myVueDropzone_vertical_url.removeFile(f));
            }
            const verticalFile = files[0] || null;
            this.verticalImage = verticalFile;
            this.verticalImageDeleted = false;
            if (verticalFile) {
                if (this.verticalImagePreviewUrl) URL.revokeObjectURL(this.verticalImagePreviewUrl);
                this.verticalImagePreviewUrl = URL.createObjectURL(verticalFile);
            }
        },
        clearVerticalImage() {
            if (this.verticalImagePreviewUrl) URL.revokeObjectURL(this.verticalImagePreviewUrl);
            this.verticalImagePreviewUrl = null;
            this.verticalImage = null;
            this.verticalImageDeleted = true;
            this.$refs.myVueDropzone_vertical_url.removeAllFiles();
        },
        normalizeImageToVertical(file, outWidth = 720, outHeight = 1280) {
            return new Promise((resolve, reject) => {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                    try {
                        const sourceWidth = img.naturalWidth || img.width;
                        const sourceHeight = img.naturalHeight || img.height;
                        if (!sourceWidth || !sourceHeight) {
                            URL.revokeObjectURL(url);
                            reject(new Error('Invalid image'));
                            return;
                        }

                        const targetRatio = outWidth / outHeight;
                        let sx = 0;
                        let sy = 0;
                        let sWidth = sourceWidth;
                        let sHeight = sourceHeight;
                        const sourceRatio = sourceWidth / sourceHeight;

                        if (sourceRatio > targetRatio) {
                            sWidth = Math.round(sourceHeight * targetRatio);
                            sx = Math.round((sourceWidth - sWidth) / 2);
                        } else if (sourceRatio < targetRatio) {
                            sHeight = Math.round(sourceWidth / targetRatio);
                            sy = Math.round((sourceHeight - sHeight) / 2);
                        }

                        const canvas = document.createElement('canvas');
                        canvas.width = outWidth;
                        canvas.height = outHeight;
                        canvas.getContext('2d').drawImage(img, sx, sy, sWidth, sHeight, 0, 0, outWidth, outHeight);
                        URL.revokeObjectURL(url);

                        canvas.toBlob((blob) => {
                            if (!blob) {
                                reject(new Error('Failed to create vertical thumbnail'));
                                return;
                            }
                            resolve(new File([blob], 'thumbnail-vertical.jpg', { type: 'image/jpeg' }));
                        }, 'image/jpeg', 0.85);
                    } catch (error) {
                        URL.revokeObjectURL(url);
                        reject(error);
                    }
                };
                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    reject(new Error('Failed to load image'));
                };
                img.src = url;
            });
        },
        generateThumbnailFromVideo(videoFile) {
            const url = URL.createObjectURL(videoFile);
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.muted = true;
            video.playsInline = true;
            video.src = url;

            video.addEventListener('loadedmetadata', () => {
                video.currentTime = Math.min(1, video.duration * 0.1);
            });

            video.addEventListener('seeked', () => {
                const sourceWidth = video.videoWidth || 0;
                const sourceHeight = video.videoHeight || 0;
                if (!sourceWidth || !sourceHeight) {
                    URL.revokeObjectURL(url);
                    return;
                }

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const isShort = this.uploadMode === 'short';

                if (isShort) {
                    // Always produce a vertical 9:16 thumbnail for shorts.
                    const targetRatio = 9 / 16;
                    let sx = 0;
                    let sy = 0;
                    let sWidth = sourceWidth;
                    let sHeight = sourceHeight;
                    const sourceRatio = sourceWidth / sourceHeight;

                    if (sourceRatio > targetRatio) {
                        sWidth = Math.round(sourceHeight * targetRatio);
                        sx = Math.round((sourceWidth - sWidth) / 2);
                    } else if (sourceRatio < targetRatio) {
                        sHeight = Math.round(sourceWidth / targetRatio);
                        sy = Math.round((sourceHeight - sHeight) / 2);
                    }

                    canvas.width = 720;
                    canvas.height = 1280;
                    ctx.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, 720, 1280);
                } else {
                    canvas.width = sourceWidth;
                    canvas.height = sourceHeight;
                    ctx.drawImage(video, 0, 0);
                }

                URL.revokeObjectURL(url);

                canvas.toBlob((blob) => {
                    if (!blob) return;
                    this.thumbnail = new File(
                        [blob],
                        isShort ? 'thumbnail-vertical.jpg' : 'thumbnail.jpg',
                        { type: 'image/jpeg' }
                    );
                    this.thumbnailPreviewUrl = canvas.toDataURL('image/jpeg');
                }, 'image/jpeg', 0.85);
            });
        },
         refreshData() {
            this.$emit("onRefresh");
        },
    }
};
</script>

<template>
    <b-modal
        :id="modalId"
        size="lg"
        v-model="showModal"
        @shown="onModalShown"
        @hidden="resetProps"
        title-class="font-18"
        :no-close-on-backdrop="uploadBusy"
        :no-close-on-esc="uploadBusy"
        :hide-header-close="uploadBusy"
    >
        <template v-slot:modal-header>
            <div class="w-100">
                <h5 class="modal-title mb-0">{{ headerTitle }}</h5>
                <p class="text-muted mb-0 mt-2">{{ headerSubtitle }}</p>
            </div>
            <button type="button" class="btn-close" @click="closeModal" :disabled="uploadBusy" aria-label="Close"></button>
        </template>
        <form @submit.prevent="uploadVideo">
            <div class="card mb-3">
                <div class="card-body py-2">
                    <h4 class="card-title mb-1">Basic Information</h4>
                    <p class="text-muted mb-3">Essential details about your video</p>
                    <div>
                        <div class="mb-2">
                            <label class="mb-1">Title *</label>
                            <b-form-input v-model="title" :state="submitted && $v.title.$error ? false : null"></b-form-input>
                        </div>
                        <validationMessages v-if="submitted" :fieldName="'Title'" :validationName="$v.title">
                        </validationMessages>
                    </div>
                    <div>
                        <div class="mb-2">
                            <label class="mb-1">Description</label>
                            <b-form-input v-model="description" :state="submitted && $v.description.$error ? false : null"></b-form-input>
                        </div>
                        <validationMessages v-if="submitted" :fieldName="'Description'" :validationName="$v.description">
                        </validationMessages>
                    </div>
                    <div>
                        <div class="mb-2">
                            <label class="mb-1">Tags</label>
                            <div class="tag-input-container" :class="{ 'is-invalid': submitted && $v.tags.$error }">
                                <span
                                    v-for="(tag, index) in tags"
                                    :key="tag"
                                    class="tag-pill"
                                >
                                    {{ tag }}
                                    <button type="button" class="tag-remove" @click="removeTag(index)">&times;</button>
                                </span>
                                <input
                                    v-model="tagInput"
                                    class="tag-text-input"
                                    placeholder="Type and press Enter or comma..."
                                    @keydown.enter.prevent="addTag"
                                    @keydown.188.prevent="addTag"
                                    @blur="tagInput.trim() && addTag()"
                                />
                            </div>
                        </div>
                        <validationMessages v-if="submitted" :fieldName="'Tags'" :validationName="$v.tags">
                        </validationMessages>
                    </div>
                    <div class="row mt-2 mb-2">
                        <div class="col-md-6">
                            <label class="mb-1">
                                <i class="uil uil-calendar-alt me-1"></i>Publish Date
                            </label>
                            <div class="position-relative">
                                <flat-pickr ref="datePicker" v-model="date" :config="dateconfig"
                                    class="form-control pe-4" name="date" @on-change="updatePublished" />
                                <i class="uil uil-calendar-alt position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                                    style="cursor: pointer; pointer-events: all;" @click="openDatePicker"></i>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <label class="mb-1">
                                <i class="uil uil-clock me-1"></i>Publish Time
                            </label>
                            <div class="position-relative">
                                <flat-pickr ref="timePicker" v-model="time" :config="timeconfig"
                                    class="form-control pe-4" name="time" @on-change="updatePublished" />
                                <i class="uil uil-clock position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                                    style="cursor: pointer; pointer-events: all;" @click="openTimePicker"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card mb-3">
                <div class="card-body py-2">
                    <h4 class="card-title mb-1">
                        <i class="uil uil-video me-2"></i>Video
                    </h4>
                    <p class="text-muted mb-3">Upload a video</p>

                    <div class="mb-3">
                        <label class="form-label small fw-medium d-block mb-2">Format</label>
                        <div class="d-flex flex-wrap gap-3" role="radiogroup" aria-label="Video format">
                            <div class="form-check">
                                <input
                                    id="format-mp4"
                                    class="form-check-input"
                                    type="radio"
                                    name="video_format"
                                    value="mp4"
                                    v-model="videoFormat"
                                    @change="onVideoFormatChange"
                                />
                                <label class="form-check-label" for="format-mp4">MP4</label>
                            </div>
                            <div class="form-check">
                                <input
                                    id="format-m3u8"
                                    class="form-check-input"
                                    type="radio"
                                    name="video_format"
                                    value="m3u8"
                                    v-model="videoFormat"
                                    @change="onVideoFormatChange"
                                />
                                <label class="form-check-label" for="format-m3u8">M3U8</label>
                            </div>
                        </div>
                        <p class="text-muted small mt-2 mb-0">
                            MP4 uploads a video file. M3U8 stores a public HLS stream URL.
                        </p>
                    </div>

                    <b-form-group v-if="videoFormat === 'mp4'" label-for="video" class="mb-3">
                        <div class="video-upload" :class="{ 'has-error': submitted && $v.video.$error }">
                            <div v-if="videoPreviewUrl" class="video-preview-container">
                                <video :src="videoPreviewUrl" controls class="video-preview"></video>
                                <div class="video-preview-meta">
                                    <span v-if="video" class="video-name">{{ video.name }}</span>
                                    <a v-if="!uploadBusy" href="#" class="remove-link" @click.prevent="clearVideo">
                                        <i class="uil uil-trash-alt me-1"></i>Remove
                                    </a>
                                </div>
                            </div>
                            <vue-dropzone v-show="!videoPreviewUrl" id="videoUploadDropzone" ref="myVueDropzone_video_url"
                                :use-custom-slot="true" :options="dropzoneOptions_video"
                                @vdropzone-files-added="selectFileVideoUrl" no-progress-bar>
                                <div class="video-upload__placeholder">
                                    <i class="uil uil-import"></i>
                                    <span>Drag and drop a video, or click to browse</span>
                                    <small>MP4 — uploads directly to storage (large files supported)</small>
                                </div>
                            </vue-dropzone>
                            <div v-if="uploadBusy && videoFormat === 'mp4'" class="mt-2">
                                <div class="progress" style="height: 8px;">
                                    <div
                                        class="progress-bar progress-bar-striped progress-bar-animated"
                                        role="progressbar"
                                        :style="{ width: savePhase === 'optimizing' ? '100%' : `${Math.max(Math.round(videoUploadProgress), videoUploadProgress > 0 ? 5 : 0)}%` }"
                                    ></div>
                                </div>
                                <small class="text-muted">
                                    <template v-if="savePhase === 'optimizing'">
                                        Upload complete. Optimizing video for mobile…
                                    </template>
                                    <template v-else-if="videoUploadProgress > 0 && videoUploadProgress < 100">
                                        Uploading video… {{ Math.round(videoUploadProgress) }}%
                                    </template>
                                    <template v-else-if="videoUploadProgress >= 100">
                                        Finalizing upload…
                                    </template>
                                    <template v-else>
                                        Preparing upload…
                                    </template>
                                </small>
                            </div>
                        </div>
                    </b-form-group>
                    <validationMessages v-if="videoFormat === 'mp4' && submitted" :fieldName="'Video'" :validationName="$v.video">
                    </validationMessages>

                    <b-form-group v-if="videoFormat === 'm3u8'" label-for="m3u8Url" class="mb-1">
                        <label class="mb-1" for="m3u8Url">M3U8 URL *</label>
                        <b-form-input
                            id="m3u8Url"
                            v-model="m3u8Url"
                            type="url"
                            placeholder="https://example.com/stream/playlist.m3u8"
                            :state="submitted && $v.m3u8Url.$error ? false : null"
                        ></b-form-input>
                        <small class="text-muted">Enter a public HTTPS URL ending with <code>.m3u8</code>.</small>
                    </b-form-group>
                    <validationMessages v-if="videoFormat === 'm3u8' && submitted" :fieldName="'M3U8 URL'" :validationName="$v.m3u8Url">
                    </validationMessages>
                </div>
            </div>

            <div class="card mb-3">
                <div class="card-body py-2">
                    <h4 class="card-title mb-1">
                        <i class="uil uil-image me-2"></i>Thumbnail
                    </h4>
                    <p class="text-muted mb-3">
                        <template v-if="uploadMode === 'short'">
                            Vertical thumbnail for your short (9:16). Auto-generated from the video when possible.
                        </template>
                        <template v-else>
                            Upload a thumbnail for your video
                        </template>
                    </p>
                    <b-form-group label-for="thumbnail" class="mb-3">
                        <div class="video-upload" :class="{ 'has-error': submitted && $v.thumbnail.$error }">
                            <div v-if="thumbnailPreviewUrl" class="thumbnail-preview-container" :class="{ 'is-vertical': uploadMode === 'short' }">
                                <img :src="thumbnailPreviewUrl" class="thumbnail-preview" :class="{ 'is-vertical': uploadMode === 'short' }" alt="thumbnail" />
                                <div class="video-preview-meta">
                                    <span v-if="thumbnail" class="video-name">{{ thumbnail.name }}</span>
                                    <a v-if="!uploadBusy" href="#" class="remove-link" @click.prevent="clearThumbnail">
                                        <i class="uil uil-trash-alt me-1"></i>Remove
                                    </a>
                                </div>
                            </div>
                            <vue-dropzone v-show="!thumbnailPreviewUrl" id="thumbnailUploadDropzone"
                                ref="myVueDropzone_thumbnail_url" :use-custom-slot="true"
                                :options="dropzoneOptions_thumbnail" @vdropzone-files-added="selectFileThumbnailUrl"
                                no-progress-bar>
                                <div class="video-upload__placeholder">
                                    <i class="uil uil-import"></i>
                                    <span>Drag and drop an image, or click to browse</span>
                                    <small v-if="uploadMode === 'short'">PNG, JPG — vertical 9:16 recommended</small>
                                    <small v-else>PNG, JPG up to 4MB</small>
                                </div>
                            </vue-dropzone>
                        </div>
                    </b-form-group>
                    <validationMessages v-if="submitted" :fieldName="'Thumbnail'" :validationName="$v.thumbnail">
                    </validationMessages>
                </div>
            </div>

            <div v-if="uploadMode === 'short'" class="card mb-3">
                <div class="card-body py-2">
                    <h4 class="card-title mb-1">
                        <i class="uil uil-image-v me-2"></i>Vertical Image
                    </h4>
                    <p class="text-muted mb-3">Optional portrait (9:16) image for the short</p>
                    <b-form-group label-for="verticalImage" class="mb-1">
                        <div class="video-upload">
                            <div v-if="verticalImagePreviewUrl" class="thumbnail-preview-container">
                                <img :src="verticalImagePreviewUrl" class="thumbnail-preview" alt="vertical image" />
                                <div class="video-preview-meta">
                                    <span v-if="verticalImage" class="video-name">{{ verticalImage.name }}</span>
                                    <a v-if="!uploadBusy" href="#" class="remove-link" @click.prevent="clearVerticalImage">
                                        <i class="uil uil-trash-alt me-1"></i>Remove
                                    </a>
                                </div>
                            </div>
                            <vue-dropzone v-show="!verticalImagePreviewUrl" id="verticalUploadDropzone"
                                ref="myVueDropzone_vertical_url" :use-custom-slot="true"
                                :options="dropzoneOptions_vertical" @vdropzone-files-added="selectFileVerticalUrl"
                                no-progress-bar>
                                <div class="video-upload__placeholder">
                                    <i class="uil uil-import"></i>
                                    <span>Drag and drop a portrait image, or click to browse</span>
                                    <small>PNG, JPG up to 4MB</small>
                                </div>
                            </vue-dropzone>
                        </div>
                    </b-form-group>
                </div>
            </div>

            <div v-if="uploadMode !== 'short'" class="card mb-3">
                <div class="card-body py-2">
                    <div class="d-flex align-items-center justify-content-between">
                        <div>
                            <h4 class="card-title mb-0">
                               Premium Content
                            </h4>
                            <p class="text-muted mb-0 mt-1" style="font-size:13px;">Restrict this video to premium subscribers only</p>
                        </div>
                        <div class="form-check form-switch form-switch-lg mb-0">
                            <input v-model="isPremium" type="checkbox" class="form-check-input" id="isPremiumSwitch" />
                        </div>
                    </div>
                </div>
            </div>

            <NotificationAudienceTargeting
                ref="audienceTargeting"
                :entity-label="uploadMode === 'short' ? 'short' : 'video'"
            />

            <input type="hidden" name="csrf_token" v-model="csrf_token">
        </form>
        <template v-slot:modal-footer>
            <b-button variant="light" @click="closeModal" :disabled="uploadBusy">Close</b-button>
            <b-button variant="primary" @click="uploadVideo" :disabled="uploadBusy">
                <b-spinner v-show="uploadBusy" small class="me-1"></b-spinner>
                {{ submitButtonLabel }}
            </b-button>
        </template>

    </b-modal>
</template>

<style scoped>
.video-upload {
    border: 2px dashed #d3d8e6;
    border-radius: 16px;
    background: #f7f8fb;
    padding: 16px;
    transition: border-color 0.2s ease, background 0.2s ease;
    overflow: hidden;
}

.video-upload.is-invalid {
    border-color: #f97373;
}

.video-upload.has-error {
    border-color: #f97373;
    background: #fff5f5;
}

.video-upload__placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #5f6b7d;
    font-size: 14px;
    padding: 24px 0;
}

.video-upload__placeholder i {
    font-size: 32px;
    color: #151a2d;
}

.video-upload__placeholder small {
    color: #a0aec0;
}

/* --- video preview --- */
.video-preview-container,
.thumbnail-preview-container {
    width: 100%;
}

.video-preview {
    width: 100%;
    max-height: 280px;
    border-radius: 8px;
    background: #000;
    display: block;
}

.thumbnail-preview {
    width: 100%;
    max-height: 220px;
    border-radius: 8px;
    display: block;
    object-fit: cover;
}

.thumbnail-preview.is-vertical {
    max-width: 220px;
    max-height: 390px;
    margin: 0 auto;
    object-fit: contain;
    background: #111;
}

.thumbnail-preview-container.is-vertical {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.video-preview-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 2px 0;
}

.video-name {
    font-size: 13px;
    color: #5f6b7d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 80%;
}

.remove-link {
    font-size: 12px;
    color: #f97373;
    text-decoration: none;
    white-space: nowrap;
}

.remove-link:hover {
    color: #e05555;
}

/* --- tag input --- */
.tag-input-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-height: 42px;
    padding: 6px 10px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background: #fff;
    cursor: text;
}

.tag-input-container.is-invalid {
    border-color: #f97373;
}

.tag-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.6;
    white-space: nowrap;
    background: #34c38f;
    color: #fff;
}

.tag-remove {
    background: none;
    border: none;
    padding: 0;
    line-height: 1;
    font-size: 14px;
    cursor: pointer;
    opacity: 0.6;
    color: inherit;
}

.tag-remove:hover {
    opacity: 1;
}

.tag-text-input {
    border: none;
    outline: none;
    font-size: 14px;
    flex: 1;
    min-width: 140px;
    background: transparent;
    color: #495057;
}

/* --- dropzone preview overrides --- */
.video-upload /deep/ .vue-dropzone {
    border: none;
    background: transparent;
    padding: 0;
    min-height: auto;
}

.video-upload /deep/ .dz-preview {
    display: block;
    width: 100%;
    margin: 0;
    min-height: auto;
}

.video-upload /deep/ .dz-image {
    width: 100%;
    height: 220px;
    border-radius: 8px;
    overflow: hidden;
}

.video-upload /deep/ .dz-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.video-upload /deep/ .dz-details {
    position: static;
    padding: 8px 4px 0;
    opacity: 1;
    background: transparent;
}

.video-upload /deep/ .dz-filename span,
.video-upload /deep/ .dz-size span {
    background: transparent;
    border: none;
    font-size: 13px;
    color: #5f6b7d;
}

.video-upload /deep/ .dz-remove {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    color: #f97373;
    text-align: center;
    text-decoration: none;
}

.video-upload /deep/ .dz-success-mark,
.video-upload /deep/ .dz-error-mark,
.video-upload /deep/ .dz-progress {
    display: none !important;
}
</style>